import { showDialog } from '@/utils/dialogs';
/**
 * TranscriptViewer
 *
 * Post-transcription review/edit/export/share view. Supporting material for an
 * existing session — does NOT trigger analysis. Analysis runs automatically
 * after upload; the user lands in the chat session, then can open this view.
 *
 * Web only (D1): two columns (transcript + "Controls" panel) from 900px;
 * below that the panel stacks under the transcript (CORE-7).
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
    View,
    Text,
    Modal,
    TouchableOpacity,
    ScrollView,
    ActivityIndicator,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useTranslation } from '../../hooks/useTranslation';
import { useResponsive } from '../../hooks/useResponsive';
import { TranscriptContent, TranscriptSegment, TranscriptListItem, TranscriptAnalysisItem, ProsodicSummary, CoachingMoment } from '../../types';
import { transcriptionAPI, chatAPI } from '../../services/api';
import { EditableTranscript } from './EditableTranscript';
import { useAccessPolicy } from '../../hooks/useAccessPolicy';
import { brand } from '../../design-system/theme/tokens';

// Design tokens
const COLORS = {
    primary: brand.primary,
    secondary: '#F59E0B',
    success: '#10B981',
    error: '#EF4444',
    background: '#FFFFFF',
    surface: '#F9FAFB',
    text: {
        primary: '#111827',
        secondary: '#4B5563',
        muted: '#9CA3AF',
    },
    border: '#E5E7EB',
    coach: brand.primary,
    client: '#F59E0B',
    laughter: '#34D399',
    voiceBreak: '#FB923C',
    emotion: '#A78BFA',
    insight: '#3B82F6',
};

const stripFileExtension = (filename?: string | null) => {
    const raw = (filename || '').trim();
    if (!raw) return '';
    const basename = raw.replace(/\\/g, '/').split('/').pop() || raw;
    return basename.replace(/\.[^/.]+$/, '').trim();
};

const sanitizeDownloadName = (name: string) =>
    name.replace(/[\x00-\x1f\x7f-\x9f<>:"/\\|?*]/g, '_').replace(/\s+/g, ' ').trim();

const buildAnalysisPdfFilename = (sourceName: string | undefined, language: string, sessionId: number) => {
    const suffix = language === 'en' ? 'analysis' : 'analiz';
    let base = stripFileExtension(sourceName) || `analysis_${sessionId}`;
    const lowerBase = base.toLocaleLowerCase(language === 'tr' ? 'tr-TR' : 'en-US');
    if (!lowerBase.includes('analysis') && !lowerBase.includes('analiz')) {
        base = `${base} ${suffix}`;
    }
    return `${sanitizeDownloadName(base) || `analysis_${sessionId}`}.pdf`;
};

interface TranscriptViewerProps {
    visible: boolean;
    onClose: () => void;
    transcript: TranscriptContent | null;
    listItem?: TranscriptListItem;
    /** Linked analyses for this transcript */
    analyses?: TranscriptAnalysisItem[];
    /** Open an analysis session by id (used for "Sohbette aç" when there's an analysis) */
    onViewAnalysis?: (sessionId: number) => void;
    /** Refresh analyses list (e.g. after re-running) */
    onRefreshAnalyses?: () => void;
    /** Optional callback to re-run analysis after the transcript was edited and saved.
     *  If provided and the transcript has linked analyses, a "Refresh feedback" CTA
     *  appears in the post-save success state. */
    onRefreshFeedback?: (documentId: number) => void;
    /** Return to the calling session/chat (closes viewer + nav back to chat). */
    onReturnToSession?: () => void;
    /** Open the linked session/chat in case the viewer was opened from outside (e.g. library). */
    onOpenInChat?: (sessionId: number) => void;
    /** Share with mentor (B2B only). When omitted, the share button is hidden even for B2B users. */
    onShareWithMentor?: (documentId: number) => void;
    /** If viewing a shared document, info about who shared it */
    sharedBy?: {
        name: string;
        date: string;
        note?: string;
    };
    /** Whether this is a shared document (hides certain actions) */
    isSharedDocument?: boolean;
    /** Callback when transcript content is updated via segment editing */
    onTranscriptUpdated?: (content: TranscriptContent) => void;
}

function formatDuration(seconds?: number): string {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatSilenceDuration(seconds: number): string {
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}m ${secs}s`;
}

function languagePillLabel(code?: string): string {
    if (!code) return '—';
    const c = code.toLowerCase();
    if (c.startsWith('tr')) return 'Türkçe';
    if (c.startsWith('en')) return 'English';
    return code.toUpperCase();
}

// ---------------------------------------------------------------------------
// Sub-components for prosodic summary & coaching insights (unchanged behaviour)
// ---------------------------------------------------------------------------

function StatRow({ label, value }: { label: string; value: string | number }) {
    return (
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 4 }}>
            <Text style={{ fontSize: 13, color: COLORS.text.secondary }}>{label}</Text>
            <Text style={{ fontSize: 13, fontWeight: '600', color: COLORS.text.primary }}>{value}</Text>
        </View>
    );
}

function ProsodicSummaryPanel({
    summary,
    t,
    diarizationMethod,
    diarizationConfidence,
    provider,
}: {
    summary: ProsodicSummary;
    t: (key: string) => string;
    diarizationMethod?: string;
    diarizationConfidence?: number;
    provider?: string;
}) {
    const wpmLabel = t('transcription.viewer.wordsPerMin');

    return (
        <View style={{ paddingHorizontal: 16, paddingBottom: 12, gap: 12 }}>
            {summary.coach && (
                <View style={{ backgroundColor: '#EFF6FF', borderRadius: 8, padding: 12 }}>
                    <Text style={{ fontSize: 13, fontWeight: '700', color: COLORS.coach, marginBottom: 6 }}>Coach</Text>
                    {summary.coach.avg_speaking_rate_wpm != null && (
                        <StatRow label={t('transcription.viewer.speakingRate')} value={`${Math.round(summary.coach.avg_speaking_rate_wpm)} ${wpmLabel}`} />
                    )}
                    {summary.coach.total_speaking_time_sec != null && (
                        <StatRow label={t('transcription.viewer.speakingTime')} value={formatSilenceDuration(summary.coach.total_speaking_time_sec)} />
                    )}
                    {summary.coach.laughter_count != null && summary.coach.laughter_count > 0 && (
                        <StatRow label={t('transcription.viewer.laughterCount')} value={summary.coach.laughter_count} />
                    )}
                </View>
            )}
            {summary.client && (
                <View style={{ backgroundColor: '#FFFBEB', borderRadius: 8, padding: 12 }}>
                    <Text style={{ fontSize: 13, fontWeight: '700', color: COLORS.client, marginBottom: 6 }}>Client</Text>
                    {summary.client.avg_speaking_rate_wpm != null && (
                        <StatRow label={t('transcription.viewer.speakingRate')} value={`${Math.round(summary.client.avg_speaking_rate_wpm)} ${wpmLabel}`} />
                    )}
                    {summary.client.total_speaking_time_sec != null && (
                        <StatRow label={t('transcription.viewer.speakingTime')} value={formatSilenceDuration(summary.client.total_speaking_time_sec)} />
                    )}
                </View>
            )}
            {(diarizationMethod || provider) && (
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12, paddingTop: 4 }}>
                    {provider && <Text style={{ fontSize: 11, color: COLORS.text.muted }}>{t('transcription.viewer.providerUsed')}: {provider}</Text>}
                    {diarizationMethod && <Text style={{ fontSize: 11, color: COLORS.text.muted }}>{t('transcription.viewer.diarizationMethod')}: {diarizationMethod}</Text>}
                    {diarizationConfidence != null && (
                        <Text style={{ fontSize: 11, color: COLORS.text.muted }}>
                            {t('transcription.viewer.diarizationConfidence')}: {(diarizationConfidence * 100).toFixed(0)}%
                        </Text>
                    )}
                </View>
            )}
        </View>
    );
}

function CoachingInsightsPanel({ moments, t }: { moments: CoachingMoment[]; t: (key: string) => string }) {
    const getInsightLabel = (type: string): string => {
        switch (type) {
            case 'powerful_question': return t('transcription.viewer.powerfulQuestion');
            case 'breakthrough': return t('transcription.viewer.breakthroughMoment');
            case 'rapport_laughter': return t('transcription.viewer.rapportLaughter');
            default: return type;
        }
    };
    const getInsightColor = (type: string): string => {
        switch (type) {
            case 'powerful_question': return COLORS.insight;
            case 'breakthrough': return COLORS.emotion;
            case 'rapport_laughter': return COLORS.laughter;
            default: return COLORS.text.secondary;
        }
    };
    const getInsightIcon = (type: string): string => {
        switch (type) {
            case 'powerful_question': return 'help-outline';
            case 'breakthrough': return 'auto-awesome';
            case 'rapport_laughter': return 'sentiment-very-satisfied';
            default: return 'info-outline';
        }
    };
    return (
        <View style={{ paddingHorizontal: 16, paddingBottom: 12, gap: 8 }}>
            {moments.map((moment, idx) => (
                <View key={idx} style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: COLORS.surface, borderRadius: 8, padding: 10, gap: 10 }}>
                    <View style={{ width: 32, height: 32, borderRadius: 16, backgroundColor: getInsightColor(moment.type) + '20', alignItems: 'center', justifyContent: 'center' }}>
                        <MaterialIcons name={getInsightIcon(moment.type) as any} size={18} color={getInsightColor(moment.type)} />
                    </View>
                    <View style={{ flex: 1 }}>
                        <Text style={{ fontSize: 13, fontWeight: '600', color: COLORS.text.primary }}>{getInsightLabel(moment.type)}</Text>
                        {moment.description && (
                            <Text style={{ fontSize: 12, color: COLORS.text.secondary, marginTop: 2 }} numberOfLines={2}>{moment.description}</Text>
                        )}
                    </View>
                    <Text style={{ fontSize: 11, color: COLORS.text.muted }}>
                        {Math.floor(moment.time / 60)}:{Math.floor(moment.time % 60).toString().padStart(2, '0')}
                    </Text>
                </View>
            ))}
        </View>
    );
}

export function TranscriptViewer({
    visible,
    onClose,
    transcript,
    listItem,
    analyses = [],
    onViewAnalysis,
    onRefreshAnalyses,
    onRefreshFeedback,
    onReturnToSession,
    onOpenInChat,
    onShareWithMentor,
    sharedBy,
    isSharedDocument = false,
    onTranscriptUpdated,
}: TranscriptViewerProps) {
    const { t, language } = useTranslation();
    const { canExportIcfPerformanceTranscript, isB2BUser } = useAccessPolicy();
    const stackWeb = useResponsive().width < 900;
    const [downloading, setDownloading] = useState<string | null>(null);
    const [exportingPdf, setExportingPdf] = useState<number | null>(null);
    const [showProsodicSummary, setShowProsodicSummary] = useState(false);
    const [showCoachingInsights, setShowCoachingInsights] = useState(false);

    // Edit-mode state
    const [editMode, setEditMode] = useState(false);
    const [editedSegments, setEditedSegments] = useState<TranscriptSegment[] | null>(null);
    const [hasChanges, setHasChanges] = useState(false);
    const [saving, setSaving] = useState(false);
    // After a successful save, show "Transkript güncellendi" + optional refresh-feedback CTA
    const [savedRecently, setSavedRecently] = useState(false);
    const [savedEditCount, setSavedEditCount] = useState(0);

    const hasContent = transcript?.raw_text || transcript?.corrected_text;
    const displayText = transcript?.corrected_text || transcript?.raw_text;
    const canEdit = !isSharedDocument && (transcript?.segments?.length ?? 0) > 0;
    const linkedSessionId = analyses[0]?.session_id;

    // Keep "saved recently" sticky until close so the success banner stays visible
    React.useEffect(() => {
        if (!visible) {
            setSavedRecently(false);
            setSavedEditCount(0);
            setEditMode(false);
            setEditedSegments(null);
            setHasChanges(false);
        }
    }, [visible]);

    const enterEditMode = useCallback(() => {
        if (!transcript?.segments) return;
        setEditedSegments(JSON.parse(JSON.stringify(transcript.segments)));
        setHasChanges(false);
        setEditMode(true);
        setSavedRecently(false);
    }, [transcript?.segments]);

    const cancelEditMode = useCallback(() => {
        setEditMode(false);
        setEditedSegments(null);
        setHasChanges(false);
    }, []);

    const handleSegmentChange = useCallback((segIndex: number, newText: string) => {
        setEditedSegments(prev => {
            if (!prev) return prev;
            const updated = [...prev];
            updated[segIndex] = { ...updated[segIndex], text: newText };
            return updated;
        });
        setHasChanges(true);
    }, []);

    const handleSpeakerToggle = useCallback((segIndex: number) => {
        const speakers = transcript?.speakers || [];
        if (speakers.length < 2) return;
        setEditedSegments(prev => {
            if (!prev) return prev;
            const updated = [...prev];
            const seg = updated[segIndex];
            const currentIdx = speakers.indexOf(seg.speaker || '');
            const nextSpeaker = speakers[(currentIdx + 1) % speakers.length];
            updated[segIndex] = { ...seg, speaker: nextSpeaker };
            return updated;
        });
        setHasChanges(true);
    }, [transcript?.speakers]);

    const handleSplitSegment = useCallback((segIndex: number, wordIndex: number) => {
        if (wordIndex === 0) return;
        setEditedSegments(prev => {
            if (!prev || segIndex < 0 || segIndex >= prev.length) return prev;
            const seg = prev[segIndex];
            const words = seg.text.split(/\s+/).filter(Boolean);
            if (wordIndex >= words.length) return prev;
            const beforeWords = words.slice(0, wordIndex);
            const afterWords = words.slice(wordIndex);
            const duration = seg.end - seg.start;
            const ratio = wordIndex / words.length;
            const splitTime = seg.start + duration * ratio;
            const newSeg1 = { ...seg, text: beforeWords.join(' '), end: splitTime };
            const newSeg2 = { ...seg, text: afterWords.join(' '), start: splitTime };
            const updated = [...prev];
            updated.splice(segIndex, 1, newSeg1, newSeg2);
            return updated;
        });
        setHasChanges(true);
    }, []);

    const handleMoveWord = useCallback((fromSeg: number, wordIndex: number, toSeg: number) => {
        setEditedSegments(prev => {
            if (!prev) return prev;
            if (fromSeg === toSeg) return prev;
            if (fromSeg < 0 || fromSeg >= prev.length) return prev;
            if (toSeg < 0 || toSeg >= prev.length) return prev;
            if (prev[toSeg].is_silence) return prev;
            const updated = [...prev];
            const srcWords = updated[fromSeg].text.split(/\s+/).filter(Boolean);
            if (wordIndex < 0 || wordIndex >= srcWords.length) return prev;
            const [movedWord] = srcWords.splice(wordIndex, 1);
            updated[fromSeg] = { ...updated[fromSeg], text: srcWords.join(' ') };
            const dstWords = updated[toSeg].text.split(/\s+/).filter(Boolean);
            if (toSeg > fromSeg) dstWords.push(movedWord);
            else dstWords.unshift(movedWord);
            updated[toSeg] = { ...updated[toSeg], text: dstWords.join(' ') };
            if (srcWords.length === 0) updated.splice(fromSeg, 1);
            return updated;
        });
        setHasChanges(true);
    }, []);

    const editsCount = useMemo(() => {
        if (!editedSegments || !transcript?.segments) return 0;
        // Count segments whose text differs from the original — proxy for "edits"
        let n = 0;
        const len = Math.min(editedSegments.length, transcript.segments.length);
        for (let i = 0; i < len; i++) {
            if (editedSegments[i].text !== transcript.segments[i].text) n++;
            else if (editedSegments[i].speaker !== transcript.segments[i].speaker) n++;
        }
        if (editedSegments.length !== transcript.segments.length) {
            n += Math.abs(editedSegments.length - transcript.segments.length);
        }
        return n;
    }, [editedSegments, transcript?.segments]);

    const handleSave = useCallback(async () => {
        if (!transcript?.id || !editedSegments) return;
        setSaving(true);
        try {
            const result = await transcriptionAPI.saveSegments(transcript.id, editedSegments);
            onTranscriptUpdated?.(result);
            const editsAtSave = editsCount;
            setEditMode(false);
            setEditedSegments(null);
            setHasChanges(false);
            setSavedRecently(true);
            setSavedEditCount(editsAtSave || 1);
        } catch (error: any) {
            console.error('Save segments error:', error);
            const msg = error?.response?.data?.detail || error?.message || 'Failed to save changes';
            showDialog(msg);
        } finally {
            setSaving(false);
        }
    }, [transcript?.id, editedSegments, onTranscriptUpdated, t, editsCount]);

    const handleDownload = async (format: 'srt' | 'vtt' | 'txt' | 'pdf' | 'icf-docx') => {
        if (!transcript?.id) return;
        setDownloading(format);
        try {
            if (format === 'icf-docx') {
                const docxBlob = await transcriptionAPI.downloadIcfDocx(transcript.id);
                const url = URL.createObjectURL(docxBlob);
                const a = document.createElement('a');
                a.href = url; a.download = 'icf_performance_transcript.docx';
                document.body.appendChild(a); a.click();
                document.body.removeChild(a); URL.revokeObjectURL(url);
            } else if (format === 'pdf') {
                const pdfBlob = await transcriptionAPI.downloadPdf(transcript.id, language);
                const url = URL.createObjectURL(pdfBlob);
                const a = document.createElement('a');
                a.href = url; a.download = 'transcript.pdf';
                document.body.appendChild(a); a.click();
                document.body.removeChild(a); URL.revokeObjectURL(url);
            } else {
                const content = await transcriptionAPI.download(transcript.id, format);
                const blob = new Blob([content], {
                    type: format === 'srt' ? 'application/x-subrip' : format === 'vtt' ? 'text/vtt' : 'text/plain'
                });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = `transcript.${format}`;
                document.body.appendChild(a); a.click();
                document.body.removeChild(a); URL.revokeObjectURL(url);
            }
        } catch (error: any) {
            console.error('Download error:', error);
            showDialog(t('transcription.errors.downloadFailed'));
        } finally {
            setDownloading(null);
        }
    };

    const handleDownloadAudio = async () => {
        if (!transcript?.id) return;
        setDownloading('audio');
        try {
            const result = await transcriptionAPI.downloadAudio(transcript.id);
            if (result.download_url) {
                window.open(result.download_url, '_blank');
            }
        } catch (error: any) {
            console.error('Audio download error:', error);
            showDialog(t('transcription.errors.downloadFailed') || 'Failed to download audio');
        } finally {
            setDownloading(null);
        }
    };

    const handleExportAnalysisPdf = async (sessionId: number) => {
        setExportingPdf(sessionId);
        try {
            const pdfBlob = await chatAPI.exportSessionPdf(sessionId, language);
            const sourceName = listItem?.original_filename || (transcript as any)?.original_filename;
            const filename = buildAnalysisPdfFilename(sourceName, language, sessionId);
            const url = URL.createObjectURL(pdfBlob);
            const a = document.createElement('a');
            a.href = url; a.download = filename;
            document.body.appendChild(a); a.click();
            document.body.removeChild(a); URL.revokeObjectURL(url);
        } catch (error: any) {
            console.error('PDF export error:', error);
            showDialog(t('common.error') || 'Failed to export PDF');
        } finally {
            setExportingPdf(null);
        }
    };

    const handleRefreshFeedback = () => {
        if (transcript?.id && onRefreshFeedback) {
            onRefreshFeedback(transcript.id);
            setSavedRecently(false);
        }
    };

    const handleOpenInChat = () => {
        if (linkedSessionId && onOpenInChat) onOpenInChat(linkedSessionId);
        else if (linkedSessionId && onViewAnalysis) onViewAnalysis(linkedSessionId);
    };

    const handleReturnToSession = () => {
        if (onReturnToSession) onReturnToSession();
        else onClose();
    };

    const filename = listItem?.original_filename || (transcript as any)?.original_filename || 'transcript';
    const langPill = languagePillLabel(transcript?.language || language);
    const speakerCount = transcript?.speakers?.length || 0;

    const replaceCount = (template: string, n: number) => template.replace('{{n}}', String(n));

    // ====================================================================
    // WEB layout — two-column design matching the redesign mockup
    // ====================================================================
    // D1: web-only; the native render tree was deleted.
      const showShareToMentor = isB2BUser && !isSharedDocument && !!onShareWithMentor;
      // CORE-7: below ~900px the controls stack under the transcript in one
      // scroller (the fixed 320px aside left ~70px for the transcript on phones).
      const BodyContainer = (stackWeb ? ScrollView : View) as React.ComponentType<any>;
      const Column = (stackWeb ? View : ScrollView) as React.ComponentType<any>;
      const columnProps = (style: object, contentStyle: object) => (
          stackWeb ? { style: [style, contentStyle] } : { style, contentContainerStyle: contentStyle }
      );

      return (
          <Modal
              visible={visible}
              animationType="fade"
              transparent
              onRequestClose={onClose}
          >
              <View style={[webStyles.backdrop, stackWeb && webStyles.backdropStacked]}>
                  <View testID="transcript_viewer" style={[webStyles.shell, stackWeb && webStyles.shellStacked]}>
                      {/* ── Top bar: breadcrumb + close ─────────────────────── */}
                      <View style={webStyles.topBar}>
                          <TouchableOpacity onPress={onClose} style={webStyles.iconButton} accessibilityLabel="Close">
                              <MaterialIcons name="close" size={20} color={COLORS.text.secondary} />
                          </TouchableOpacity>
                          <View style={webStyles.breadcrumb}>
                              <Text style={webStyles.breadcrumbBase}>{t('transcription.viewer.breadcrumbSessions')}</Text>
                              <Text style={webStyles.breadcrumbDivider}> / </Text>
                              <Text style={webStyles.breadcrumbCurrent} numberOfLines={1}>{filename}</Text>
                          </View>
                      </View>

                      {/* ── Two-column body ─────────────────────────────────── */}
                      <BodyContainer testID="transcript_viewer_body" style={stackWeb ? webStyles.bodyStacked : webStyles.body}>
                          <Column {...columnProps(stackWeb ? webStyles.mainStacked : webStyles.main, { padding: stackWeb ? 16 : 28, paddingTop: 18 })}>
                              {/* Header */}
                              <View style={webStyles.headerRow}>
                                  <View style={{ flex: 1 }}>
                                      <Text style={webStyles.title}>
                                          {t('transcription.viewer.headerTitleReady')}
                                      </Text>
                                      <Text style={webStyles.subtitle}>
                                          {t('transcription.viewer.headerSubtitle')}
                                      </Text>
                                  </View>
                                  <View style={webStyles.headerActions}>
                                      <TouchableOpacity
                                          style={webStyles.headerOutlineBtn}
                                          onPress={() => handleDownload('pdf')}
                                          disabled={downloading !== null}
                                      >
                                          <MaterialIcons name="file-download" size={16} color={COLORS.primary} />
                                          <Text style={webStyles.headerOutlineBtnText}>
                                              {downloading === 'pdf' ? '...' : t('transcription.viewer.download')}
                                          </Text>
                                      </TouchableOpacity>
                                      {canEdit && !editMode && (
                                          <TouchableOpacity style={webStyles.headerPrimaryBtn} onPress={enterEditMode}>
                                              <MaterialIcons name="edit" size={16} color="#FFFFFF" />
                                              <Text style={webStyles.headerPrimaryBtnText}>
                                                  {t('transcription.viewer.tabEdit')}
                                              </Text>
                                          </TouchableOpacity>
                                      )}
                                  </View>
                              </View>

                              {/* Status pills */}
                              <View style={webStyles.pillRow}>
                                  <View style={webStyles.pill}>
                                      <MaterialIcons name="check-circle" size={14} color={COLORS.success} />
                                      <Text style={webStyles.pillText}>{t('transcription.viewer.pillSaved')}</Text>
                                  </View>
                                  <View style={webStyles.pill}>
                                      <MaterialIcons name="schedule" size={14} color={COLORS.text.secondary} />
                                      <Text style={webStyles.pillText}>{formatDuration(transcript?.duration_seconds)}</Text>
                                  </View>
                                  <View style={webStyles.pill}>
                                      <MaterialIcons name="people" size={14} color={COLORS.text.secondary} />
                                      <Text style={webStyles.pillText}>
                                          {replaceCount(t('transcription.viewer.pillSpeakers'), speakerCount)}
                                      </Text>
                                  </View>
                                  <View style={webStyles.pill}>
                                      <MaterialIcons name="language" size={14} color={COLORS.text.secondary} />
                                      <Text style={webStyles.pillText}>{langPill}</Text>
                                  </View>
                              </View>

                              {/* Shared-by banner */}
                              {sharedBy && (
                                  <View style={webStyles.sharedByBanner}>
                                      <MaterialIcons name="share" size={16} color={COLORS.primary} />
                                      <Text style={webStyles.sharedByText}>
                                          {t('library.sharedBy').replace('{{name}}', sharedBy.name)}
                                      </Text>
                                  </View>
                              )}

                              {/* Post-save success banner */}
                              {savedRecently && (
                                  <View style={webStyles.updatedBanner}>
                                      <MaterialIcons name="check-circle" size={18} color={COLORS.success} />
                                      <View style={{ flex: 1 }}>
                                          <Text style={webStyles.updatedBannerTitle}>
                                              {t('transcription.viewer.transcriptUpdated')}
                                          </Text>
                                          <Text style={webStyles.updatedBannerSub}>
                                              {replaceCount(t('transcription.viewer.editsCount'), savedEditCount)}
                                          </Text>
                                      </View>
                                      {onRefreshFeedback && analyses.length > 0 && (
                                          <TouchableOpacity style={webStyles.updatedBannerCta} onPress={handleRefreshFeedback}>
                                              <MaterialIcons name="autorenew" size={14} color="#FFFFFF" />
                                              <Text style={webStyles.updatedBannerCtaText}>
                                                  {t('transcription.viewer.refreshFeedback')}
                                              </Text>
                                          </TouchableOpacity>
                                      )}
                                  </View>
                              )}

                              {/* Transcript card */}
                              <View style={webStyles.transcriptCard}>
                                  <Text style={webStyles.cardHeading}>{t('transcription.viewer.title')}</Text>

                                  {/* Tabs Oku / Düzenle */}
                                  <View style={webStyles.tabRow}>
                                      <TouchableOpacity
                                          onPress={() => editMode && cancelEditMode()}
                                          style={[webStyles.tab, !editMode && webStyles.tabActive]}
                                      >
                                          <Text style={[webStyles.tabText, !editMode && webStyles.tabTextActive]}>
                                              {t('transcription.viewer.tabRead')}
                                          </Text>
                                      </TouchableOpacity>
                                      <TouchableOpacity
                                          onPress={() => !editMode && canEdit && enterEditMode()}
                                          style={[webStyles.tab, editMode && webStyles.tabActive, !canEdit && { opacity: 0.4 }]}
                                          disabled={!canEdit}
                                      >
                                          <Text style={[webStyles.tabText, editMode && webStyles.tabTextActive]}>
                                              {t('transcription.viewer.tabEdit')}
                                          </Text>
                                      </TouchableOpacity>
                                  </View>

                                  {/* Body */}
                                  <View style={{ marginTop: 16 }}>
                                      {editMode && editedSegments ? (
                                          <EditableTranscript
                                              segments={editedSegments}
                                              onSegmentChange={handleSegmentChange}
                                              onSpeakerToggle={handleSpeakerToggle}
                                              onSplitSegment={handleSplitSegment}
                                              onMoveWord={handleMoveWord}
                                              speakers={transcript?.speakers || []}
                                          />
                                      ) : transcript?.segments && transcript.segments.length > 0 ? (
                                          <ReadOnlySegments segments={transcript.segments} />
                                      ) : hasContent ? (
                                          <Text style={{ fontSize: 15, lineHeight: 24, color: COLORS.text.primary }}>{displayText}</Text>
                                      ) : (
                                          <View style={{ alignItems: 'center', paddingVertical: 48 }}>
                                              <MaterialIcons name="article" size={48} color={COLORS.border} />
                                              <Text style={{ fontSize: 15, color: COLORS.text.muted, marginTop: 12 }}>
                                                  {t('transcription.viewer.noContent')}
                                              </Text>
                                          </View>
                                      )}
                                  </View>

                                  {/* Edit-mode footer */}
                                  {editMode && (
                                      <View style={webStyles.editFooter}>
                                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                              <MaterialIcons name="edit" size={16} color={COLORS.secondary} />
                                              <Text style={webStyles.editFooterText}>
                                                  {replaceCount(t('transcription.viewer.editPendingCount'), editsCount)}
                                              </Text>
                                          </View>
                                          <View style={{ flexDirection: 'row', gap: 8 }}>
                                              <TouchableOpacity style={webStyles.ghostBtn} onPress={cancelEditMode}>
                                                  <Text style={webStyles.ghostBtnText}>
                                                      {t('transcription.viewer.cancelEdits')}
                                                  </Text>
                                              </TouchableOpacity>
                                              <TouchableOpacity
                                                  style={[webStyles.headerPrimaryBtn, (saving || !hasChanges) && { opacity: 0.55 }]}
                                                  onPress={handleSave}
                                                  disabled={saving || !hasChanges}
                                              >
                                                  {saving ? (
                                                      <ActivityIndicator size="small" color="#FFFFFF" />
                                                  ) : (
                                                      <Text style={webStyles.headerPrimaryBtnText}>
                                                          {t('transcription.viewer.saveChanges')}
                                                      </Text>
                                                  )}
                                              </TouchableOpacity>
                                          </View>
                                      </View>
                                  )}
                              </View>

                              {/* Optional collapsibles preserved */}
                              {transcript?.prosodic_summary && (
                                  <View style={webStyles.collapseCard}>
                                      <TouchableOpacity onPress={() => setShowProsodicSummary(v => !v)} style={webStyles.collapseHeader}>
                                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                              <MaterialIcons name="equalizer" size={18} color={COLORS.emotion} />
                                              <Text style={webStyles.collapseTitle}>{t('transcription.viewer.prosodicSummary')}</Text>
                                          </View>
                                          <MaterialIcons name={showProsodicSummary ? 'expand-less' : 'expand-more'} size={20} color={COLORS.text.muted} />
                                      </TouchableOpacity>
                                      {showProsodicSummary && (
                                          <ProsodicSummaryPanel
                                              summary={transcript.prosodic_summary}
                                              t={t}
                                              diarizationMethod={transcript.diarization_method}
                                              diarizationConfidence={transcript.diarization_confidence}
                                              provider={transcript.provider}
                                          />
                                      )}
                                  </View>
                              )}
                              {transcript?.prosodic_summary?.session?.coaching_moments && transcript.prosodic_summary.session.coaching_moments.length > 0 && (
                                  <View style={webStyles.collapseCard}>
                                      <TouchableOpacity onPress={() => setShowCoachingInsights(v => !v)} style={webStyles.collapseHeader}>
                                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                              <MaterialIcons name="lightbulb" size={18} color={COLORS.secondary} />
                                              <Text style={webStyles.collapseTitle}>
                                                  {t('transcription.viewer.coachingInsights')} ({transcript.prosodic_summary.session.coaching_moments.length})
                                              </Text>
                                          </View>
                                          <MaterialIcons name={showCoachingInsights ? 'expand-less' : 'expand-more'} size={20} color={COLORS.text.muted} />
                                      </TouchableOpacity>
                                      {showCoachingInsights && (
                                          <CoachingInsightsPanel moments={transcript.prosodic_summary.session.coaching_moments} t={t} />
                                      )}
                                  </View>
                              )}
                          </Column>

                          {/* ── Right side panel: Kontroller ────────────────── */}
                          <Column {...columnProps(stackWeb ? webStyles.asideStacked : webStyles.aside, { padding: stackWeb ? 16 : 22, gap: 16 })}>
                              <Text style={webStyles.asideTitle}>{t('transcription.viewer.controlsTitle')}</Text>

                              {/* Speakers */}
                              <View style={webStyles.asideCard}>
                                  <Text style={webStyles.asideCardTitle}>{t('transcription.viewer.speakersHeading')}</Text>
                                  <SpeakerRow color={COLORS.coach} label={t('transcription.viewer.coachLabel')} actionLabel={t('transcription.viewer.changeLabel')} />
                                  <SpeakerRow color={COLORS.client} label={t('transcription.viewer.clientLabel')} actionLabel={t('transcription.viewer.changeLabel')} />
                                  <TouchableOpacity style={webStyles.outlineRowBtn}>
                                      <Text style={webStyles.outlineRowBtnText}>{t('transcription.viewer.swapSpeakers')}</Text>
                                  </TouchableOpacity>
                              </View>

                              {/* Export */}
                              <View style={webStyles.asideCard}>
                                  <Text style={webStyles.asideCardTitle}>{t('transcription.viewer.exportHeading')}</Text>
                                  <View style={webStyles.exportGrid}>
                                      {(['pdf', 'txt', canExportIcfPerformanceTranscript ? 'icf-docx' : 'srt', 'srt'] as const)
                                          .filter((v, i, a) => a.indexOf(v) === i)
                                          .slice(0, 4)
                                          .map((fmt) => (
                                              <TouchableOpacity
                                                  key={fmt}
                                                  style={webStyles.exportTile}
                                                  onPress={() => handleDownload(fmt as any)}
                                                  disabled={downloading !== null}
                                              >
                                                  <MaterialIcons name="description" size={16} color={COLORS.text.secondary} />
                                                  <Text style={webStyles.exportTileText}>
                                                      {fmt === 'icf-docx' ? 'DOCX' : (fmt as string).toUpperCase()}
                                                  </Text>
                                              </TouchableOpacity>
                                          ))}
                                  </View>
                              </View>

                              {/* Share — B2B only */}
                              {showShareToMentor && (
                                  <View style={webStyles.asideCard}>
                                      <Text style={webStyles.asideCardTitle}>{t('transcription.viewer.shareHeading')}</Text>
                                      <TouchableOpacity
                                          style={webStyles.outlineRowBtn}
                                          onPress={() => transcript?.id && onShareWithMentor && onShareWithMentor(transcript.id)}
                                      >
                                          <MaterialIcons name="share" size={14} color={COLORS.primary} />
                                          <Text style={[webStyles.outlineRowBtnText, { marginLeft: 6 }]}>
                                              {t('library.shareWithMentor')}
                                          </Text>
                                      </TouchableOpacity>
                                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 8 }}>
                                          <MaterialIcons name="check-circle" size={14} color={COLORS.success} />
                                          <Text style={webStyles.asideMeta}>{t('transcription.viewer.savedToLibrary')}</Text>
                                      </View>
                                  </View>
                              )}

                              {/* Navigation back to session */}
                              {(onReturnToSession || (onOpenInChat && linkedSessionId)) && (
                                  <View style={webStyles.asideCard}>
                                      {onReturnToSession && (
                                          <TouchableOpacity style={webStyles.navBtn} onPress={handleReturnToSession}>
                                              <MaterialIcons name="arrow-back" size={16} color={COLORS.primary} />
                                              <Text style={webStyles.navBtnText}>
                                                  {t('transcription.viewer.backToSession')}
                                              </Text>
                                          </TouchableOpacity>
                                      )}
                                      {onOpenInChat && linkedSessionId && (
                                          <TouchableOpacity style={webStyles.navBtn} onPress={handleOpenInChat}>
                                              <MaterialIcons name="chat" size={16} color={COLORS.primary} />
                                              <Text style={webStyles.navBtnText}>
                                                  {t('transcription.viewer.openInChat')}
                                              </Text>
                                          </TouchableOpacity>
                                      )}
                                  </View>
                              )}

                              {/* History */}
                              <View style={webStyles.asideCard}>
                                  <Text style={webStyles.asideCardTitle}>{t('transcription.viewer.historyHeading')}</Text>
                                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                                      <MaterialIcons name="schedule" size={14} color={COLORS.text.muted} />
                                      <Text style={webStyles.asideMeta}>{t('transcription.viewer.lastSavedJustNow')}</Text>
                                  </View>
                                  {savedEditCount > 0 && (
                                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 6 }}>
                                          <MaterialIcons name="edit" size={14} color={COLORS.text.muted} />
                                          <Text style={webStyles.asideMeta}>
                                              {replaceCount(t('transcription.viewer.editsCount'), savedEditCount)}
                                          </Text>
                                      </View>
                                  )}
                              </View>

                              {/* Linked analyses (if any) */}
                              {analyses.length > 0 && (
                                  <View style={webStyles.asideCard}>
                                      <Text style={webStyles.asideCardTitle}>
                                          {t('analysis.analysesSection')} ({analyses.length})
                                      </Text>
                                      {analyses.map((a) => (
                                          <View key={a.session_id} style={webStyles.analysisItem}>
                                              <Text style={webStyles.analysisTitle} numberOfLines={1}>{a.title}</Text>
                                              <View style={{ flexDirection: 'row', gap: 6, marginTop: 6 }}>
                                                  {onViewAnalysis && (
                                                      <TouchableOpacity
                                                          onPress={() => onViewAnalysis(a.session_id)}
                                                          style={[webStyles.analysisBtn, { backgroundColor: COLORS.success }]}
                                                      >
                                                          <Text style={webStyles.analysisBtnText}>{t('common.view') || 'View'}</Text>
                                                      </TouchableOpacity>
                                                  )}
                                                  <TouchableOpacity
                                                      onPress={() => handleExportAnalysisPdf(a.session_id)}
                                                      disabled={exportingPdf !== null}
                                                      style={[webStyles.analysisBtn, { backgroundColor: COLORS.primary }]}
                                                  >
                                                      <Text style={webStyles.analysisBtnText}>
                                                          {exportingPdf === a.session_id ? '...' : 'PDF'}
                                                      </Text>
                                                  </TouchableOpacity>
                                              </View>
                                          </View>
                                      ))}
                                      {onRefreshAnalyses && (
                                          <TouchableOpacity onPress={onRefreshAnalyses} style={{ marginTop: 6 }}>
                                              <Text style={[webStyles.asideMeta, { color: COLORS.primary }]}>↻ {t('common.refresh')}</Text>
                                          </TouchableOpacity>
                                      )}
                                  </View>
                              )}

                              {/* Secure footer */}
                              <View style={webStyles.secureCard}>
                                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                      <MaterialIcons name="security" size={16} color={COLORS.primary} />
                                      <Text style={webStyles.secureTitle}>{t('transcription.viewer.secureNoticeTitle')}</Text>
                                  </View>
                                  <Text style={webStyles.secureBody}>
                                      {t('transcription.viewer.secureNoticeBody')}
                                  </Text>
                              </View>
                          </Column>
                      </BodyContainer>
                  </View>
              </View>
          </Modal>
      );
}

// ---------------------------------------------------------------------------
// Read-only segment list — used in "Oku" tab on web. Mirrors the mockup style:
// timestamp · speaker pill · confidence% · text · 3-dot menu.
// ---------------------------------------------------------------------------
function ReadOnlySegments({ segments }: { segments: TranscriptSegment[] }) {
    const speakerColor = (sp?: string): { bg: string; text: string; label: string } => {
        const s = (sp || '').toLowerCase();
        if (s.includes('coach') || s.includes('koç') || s.includes('koc')) {
            return { bg: COLORS.coach, text: '#FFFFFF', label: 'Koç' };
        }
        if (s.includes('client') || s.includes('müşteri') || s.includes('musteri')) {
            return { bg: '#FBBF24', text: '#78350F', label: 'Müşteri' };
        }
        return { bg: '#E5E7EB', text: COLORS.text.primary, label: sp || 'Konuşmacı' };
    };

    const fmtTs = (s: number) => {
        const m = Math.floor(s / 60);
        const ss = Math.floor(s % 60);
        return `${m.toString().padStart(2, '0')}:${ss.toString().padStart(2, '0')}`;
    };

    return (
        <View style={{ gap: 10 }}>
            {segments.map((seg, idx) => {
                if (seg.is_silence) return null;
                const sp = speakerColor(seg.speaker);
                const conf = (seg as any).confidence ?? (seg as any).avg_logprob;
                const confPct = typeof conf === 'number' ? Math.max(0, Math.min(100, Math.round(conf > 1 ? conf : conf * 100))) : null;
                return (
                    <View key={idx} style={segmentStyles.row}>
                        <Text style={segmentStyles.ts}>{fmtTs(seg.start)}</Text>
                        <View style={[segmentStyles.speakerPill, { backgroundColor: sp.bg }]}>
                            <Text style={[segmentStyles.speakerPillText, { color: sp.text }]}>{sp.label}</Text>
                        </View>
                        {confPct !== null && (
                            <View style={segmentStyles.confPill}>
                                <MaterialIcons name="check-circle" size={12} color={COLORS.success} />
                                <Text style={segmentStyles.confText}>%{confPct}</Text>
                            </View>
                        )}
                        <Text style={segmentStyles.body}>{seg.text}</Text>
                    </View>
                );
            })}
        </View>
    );
}

function SpeakerRow({ color, label, actionLabel }: { color: string; label: string; actionLabel: string }) {
    return (
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <View style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: color }} />
                <Text style={{ fontSize: 14, fontWeight: '600', color: COLORS.text.primary }}>{label}</Text>
            </View>
            <TouchableOpacity style={webStyles.outlinePillSmall}>
                <Text style={webStyles.outlinePillSmallText}>{actionLabel}</Text>
            </TouchableOpacity>
        </View>
    );
}

export default TranscriptViewer;

// ---------------------------------------------------------------------------
// Styles — split into web layout + segment row styles
// ---------------------------------------------------------------------------
const webStyles = {
    backdrop: {
        flex: 1,
        alignItems: 'center' as const,
        justifyContent: 'center' as const,
        backgroundColor: 'rgba(15, 23, 42, 0.45)',
        padding: 24,
    },
    backdropStacked: { padding: 0 },
    shellStacked: {
        width: '100%' as any,
        height: '100%' as any,
        maxHeight: '100%' as any,
        borderRadius: 0,
        borderWidth: 0,
    },
    bodyStacked: { flex: 1, minHeight: 0 },
    mainStacked: { backgroundColor: COLORS.surface },
    asideStacked: {
        width: '100%' as any,
        backgroundColor: '#FFFFFF',
        borderTopWidth: 1,
        borderTopColor: COLORS.border,
    },
    shell: {
        width: 'min(1280px, 96vw)' as any,
        height: '92vh' as any,
        maxHeight: '92vh' as any,
        borderRadius: 14,
        backgroundColor: '#FFFFFF',
        borderWidth: 1,
        borderColor: COLORS.border,
        overflow: 'hidden' as const,
        boxShadow: '0 30px 60px rgba(15, 23, 42, 0.2)' as any,
        flexDirection: 'column' as const,
    },
    topBar: {
        flexDirection: 'row' as const,
        alignItems: 'center' as const,
        gap: 12,
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderBottomWidth: 1,
        borderBottomColor: COLORS.border,
        backgroundColor: '#FFFFFF',
    },
    iconButton: {
        width: 32, height: 32, borderRadius: 8,
        alignItems: 'center' as const, justifyContent: 'center' as const,
    },
    breadcrumb: { flexDirection: 'row' as const, alignItems: 'center' as const, flex: 1 },
    breadcrumbBase: { fontSize: 13, color: COLORS.primary, fontWeight: '600' as const },
    breadcrumbDivider: { fontSize: 13, color: COLORS.text.muted },
    breadcrumbCurrent: { fontSize: 13, color: COLORS.text.secondary, flexShrink: 1 },
    body: {
        flex: 1,
        flexDirection: 'row' as const,
        minHeight: 0,
    },
    main: {
        flex: 1,
        backgroundColor: COLORS.surface,
        borderRightWidth: 1,
        borderRightColor: COLORS.border,
    },
    aside: {
        width: 320,
        backgroundColor: '#FFFFFF',
    },
    headerRow: {
        flexDirection: 'row' as const,
        flexWrap: 'wrap' as const,
        alignItems: 'flex-start' as const,
        gap: 16,
        marginBottom: 16,
    },
    title: { fontSize: 26, fontWeight: '800' as const, color: COLORS.text.primary, letterSpacing: -0.3 },
    subtitle: { fontSize: 14, color: COLORS.text.secondary, marginTop: 4, lineHeight: 20 },
    headerActions: { flexDirection: 'row' as const, alignItems: 'center' as const, gap: 8 },
    headerOutlineBtn: {
        flexDirection: 'row' as const, alignItems: 'center' as const, gap: 6,
        paddingHorizontal: 14, paddingVertical: 9, borderRadius: 8,
        borderWidth: 1, borderColor: COLORS.primary, backgroundColor: '#FFFFFF',
    },
    headerOutlineBtnText: { color: COLORS.primary, fontSize: 13, fontWeight: '700' as const },
    headerPrimaryBtn: {
        flexDirection: 'row' as const, alignItems: 'center' as const, gap: 6,
        paddingHorizontal: 14, paddingVertical: 9, borderRadius: 8,
        backgroundColor: COLORS.primary,
    },
    headerPrimaryBtnText: { color: '#FFFFFF', fontSize: 13, fontWeight: '700' as const },
    pillRow: { flexDirection: 'row' as const, flexWrap: 'wrap' as const, gap: 8, marginBottom: 16 },
    pill: {
        flexDirection: 'row' as const, alignItems: 'center' as const, gap: 6,
        paddingHorizontal: 12, paddingVertical: 6, borderRadius: 999,
        borderWidth: 1, borderColor: COLORS.border, backgroundColor: '#FFFFFF',
    },
    pillText: { fontSize: 12, fontWeight: '600' as const, color: COLORS.text.secondary },
    sharedByBanner: {
        flexDirection: 'row' as const, alignItems: 'center' as const, gap: 8,
        backgroundColor: '#EFF6FF', borderRadius: 10, padding: 12, marginBottom: 12,
        borderWidth: 1, borderColor: '#BFDBFE',
    },
    sharedByText: { fontSize: 13, color: COLORS.text.primary, fontWeight: '600' as const },
    updatedBanner: {
        flexDirection: 'row' as const, alignItems: 'center' as const, gap: 12,
        backgroundColor: '#ECFDF5', borderRadius: 10, padding: 14, marginBottom: 12,
        borderWidth: 1, borderColor: '#A7F3D0',
    },
    updatedBannerTitle: { fontSize: 14, fontWeight: '700' as const, color: COLORS.text.primary },
    updatedBannerSub: { fontSize: 12, color: COLORS.text.secondary, marginTop: 2 },
    updatedBannerCta: {
        flexDirection: 'row' as const, alignItems: 'center' as const, gap: 6,
        backgroundColor: COLORS.primary, paddingHorizontal: 12, paddingVertical: 8, borderRadius: 7,
    },
    updatedBannerCtaText: { color: '#FFFFFF', fontWeight: '700' as const, fontSize: 12 },
    transcriptCard: {
        backgroundColor: '#FFFFFF', borderRadius: 12, borderWidth: 1, borderColor: COLORS.border,
        padding: 18, marginBottom: 16,
    },
    cardHeading: { fontSize: 15, fontWeight: '700' as const, color: COLORS.text.primary, marginBottom: 12 },
    tabRow: { flexDirection: 'row' as const, gap: 6 },
    tab: {
        paddingHorizontal: 14, paddingVertical: 7, borderRadius: 7,
        backgroundColor: COLORS.surface,
    },
    tabActive: { backgroundColor: COLORS.primary },
    tabText: { fontSize: 13, fontWeight: '700' as const, color: COLORS.text.secondary },
    tabTextActive: { color: '#FFFFFF' },
    editFooter: {
        marginTop: 14, paddingTop: 12, borderTopWidth: 1, borderTopColor: COLORS.border,
        flexDirection: 'row' as const, alignItems: 'center' as const, justifyContent: 'space-between' as const,
    },
    editFooterText: { fontSize: 13, color: COLORS.text.secondary, fontWeight: '600' as const },
    ghostBtn: {
        paddingHorizontal: 14, paddingVertical: 9, borderRadius: 8,
        borderWidth: 1, borderColor: COLORS.border, backgroundColor: '#FFFFFF',
    },
    ghostBtnText: { fontSize: 13, fontWeight: '700' as const, color: COLORS.text.secondary },
    collapseCard: {
        backgroundColor: '#FFFFFF', borderRadius: 10, borderWidth: 1, borderColor: COLORS.border, marginBottom: 10,
        overflow: 'hidden' as const,
    },
    collapseHeader: {
        flexDirection: 'row' as const, alignItems: 'center' as const, justifyContent: 'space-between' as const,
        paddingHorizontal: 14, paddingVertical: 12,
    },
    collapseTitle: { fontSize: 14, fontWeight: '700' as const, color: COLORS.text.primary },
    asideTitle: { fontSize: 16, fontWeight: '800' as const, color: COLORS.text.primary, marginBottom: 4 },
    asideCard: {
        borderWidth: 1, borderColor: COLORS.border, borderRadius: 10, padding: 14, gap: 6,
    },
    asideCardTitle: { fontSize: 13, fontWeight: '700' as const, color: COLORS.text.primary, marginBottom: 4 },
    asideMeta: { fontSize: 12, color: COLORS.text.secondary },
    outlineRowBtn: {
        flexDirection: 'row' as const, alignItems: 'center' as const, justifyContent: 'center' as const,
        marginTop: 10, paddingVertical: 9, borderRadius: 8,
        borderWidth: 1, borderColor: COLORS.primary, backgroundColor: '#FFFFFF',
    },
    outlineRowBtnText: { color: COLORS.primary, fontSize: 13, fontWeight: '700' as const },
    outlinePillSmall: {
        paddingHorizontal: 10, paddingVertical: 5, borderRadius: 6,
        borderWidth: 1, borderColor: COLORS.primary, backgroundColor: '#FFFFFF',
    },
    outlinePillSmallText: { color: COLORS.primary, fontSize: 11, fontWeight: '700' as const },
    exportGrid: { flexDirection: 'row' as const, flexWrap: 'wrap' as const, gap: 6, marginTop: 6 },
    exportTile: {
        flexBasis: '48%' as any, flexGrow: 1,
        flexDirection: 'row' as const, alignItems: 'center' as const, gap: 6,
        paddingHorizontal: 10, paddingVertical: 8, borderRadius: 7,
        borderWidth: 1, borderColor: COLORS.border, backgroundColor: '#FFFFFF',
    },
    exportTileText: { fontSize: 12, fontWeight: '700' as const, color: COLORS.text.primary },
    navBtn: {
        flexDirection: 'row' as const, alignItems: 'center' as const, gap: 8,
        paddingVertical: 9, paddingHorizontal: 10, borderRadius: 8, marginTop: 6,
    },
    navBtnText: { fontSize: 13, color: COLORS.primary, fontWeight: '700' as const },
    secureCard: {
        borderRadius: 10, borderWidth: 1, borderColor: '#FDE68A',
        backgroundColor: '#FFFBEB', padding: 12, gap: 6,
    },
    secureTitle: { fontSize: 13, fontWeight: '800' as const, color: COLORS.text.primary },
    secureBody: { fontSize: 12, color: COLORS.text.secondary, lineHeight: 17 },
    analysisItem: {
        marginTop: 10, paddingTop: 10, borderTopWidth: 1, borderTopColor: COLORS.border,
    },
    analysisTitle: { fontSize: 12, fontWeight: '700' as const, color: COLORS.text.primary },
    analysisBtn: {
        paddingHorizontal: 8, paddingVertical: 4, borderRadius: 5,
    },
    analysisBtnText: { fontSize: 11, fontWeight: '700' as const, color: '#FFFFFF' },
};

const segmentStyles = {
    row: {
        flexDirection: 'row' as const,
        alignItems: 'center' as const,
        gap: 10,
        backgroundColor: '#FFFFFF',
        borderRadius: 10,
        borderWidth: 1,
        borderColor: COLORS.border,
        paddingHorizontal: 14,
        paddingVertical: 12,
    },
    ts: {
        fontSize: 12, fontWeight: '700' as const, color: COLORS.text.muted,
        minWidth: 44,
    },
    speakerPill: {
        paddingHorizontal: 9, paddingVertical: 4, borderRadius: 6,
    },
    speakerPillText: { fontSize: 11, fontWeight: '800' as const },
    confPill: {
        flexDirection: 'row' as const, alignItems: 'center' as const, gap: 3,
        paddingHorizontal: 7, paddingVertical: 3, borderRadius: 5,
        backgroundColor: '#ECFDF5',
    },
    confText: { fontSize: 11, fontWeight: '700' as const, color: COLORS.success },
    body: {
        flex: 1,
        fontSize: 14, lineHeight: 21, color: COLORS.text.primary,
    },
};
